import { useSyncExternalStore } from 'react';
import { campaignKey, createDataLayer } from '@signalvote/data';
import { useCampaignStore } from '@/stores';

function subscribeOnline(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

function useCampaignId(): string | null {
  return useCampaignStore((s) => s.selectedCampaign?.id ?? null);
}

/** The web's `defineQuery`: keys and fetches scoped to the selected campaign, idle until one is selected. */
export const { defineQuery, defineInfiniteQuery } = createDataLayer({ useCampaignId, useOnline, requireCampaign: true });

/**
 * The cache key a defined query holds for the selected campaign, for the invalidations and cache
 * writes outside a render. It reads the same store field `useCampaignId` does.
 */
export function selectedCampaignKey(key: readonly unknown[]): unknown[] {
  return campaignKey(useCampaignStore.getState().selectedCampaign?.id, key);
}

/**
 * The request headers a definition's fetch sends. `authenticatedFetch` lets a caller's headers
 * win over the store's, so the request asks for the campaign the cache key names even when the
 * store moved on mid-flight.
 */
export function campaignHeaders(campaignId: string | null): Record<string, string> {
  return campaignId ? { 'X-Campaign-Id': campaignId } : {};
}
